import React from "react";
import { Close } from "../../Icons";
import { Button } from "../Button";
import { Input, type InputProps } from "../Input";

type DebouncedInputProps = InputProps & {
  waitMs?: number;
  value: string;
  onValueChange: (value: string) => void;
};

export function DebouncedInput({
  waitMs = 200,
  value: initialValue,
  onValueChange,
  ...inputProps
}: Readonly<DebouncedInputProps>) {
  const [value, setValue] = React.useState(initialValue);
  const [hasFocus, setHasFocus] = React.useState(false);

  const [prev, setPrev] = React.useState({ initialValue, hasFocus });
  if (prev.initialValue !== initialValue || prev.hasFocus !== hasFocus) {
    setPrev({ initialValue, hasFocus });
    if (!hasFocus) setValue(initialValue);
  }

  const onChangeHandler = React.useEffectEvent((next: string) => {
    if (next !== initialValue) onValueChange(next);
  });
  React.useEffect(() => {
    const timeout = setTimeout(onChangeHandler, waitMs, value);
    return () => clearTimeout(timeout);
  }, [value, waitMs]);

  return (
    <Input
      value={value}
      onChange={(e) => {
        const value = e.target.value;
        if (inputProps.type === "number" || (inputProps.inputMode === "numeric" && Number.isNaN(Number(value)))) {
          return;
        }
        setValue(e.target.value);
      }}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      {...inputProps}
      endAdornment={
        value ? (
          <Button
            icon
            onClick={() => {
              setValue("");
              onValueChange("");
            }}
          >
            <Close />
          </Button>
        ) : null
      }
    />
  );
}
